<template>
  <PageModal content-class="max-w-[700px]" @close="$emit('close')">
    <div v-if="!success">
      <h2 class="mb-16 text-3xl text-black">
        {{ $texts('pdp.productNotification', 'Product notification') }}
      </h2>
      <div class="mb-56 text-grey-dark-01">
        {{
          $texts(
            'pdp.textProductSoldOut',
            'The product you are looking for is sold out. Enter your email address so that we can notify you when the product is available online again.',
          )
        }}
      </div>
      <FormKit
        ref="formKit"
        type="form"
        class="mb-64"
        :incomplete-message="
          $texts(
            'form.incompleteMessage',
            'Sorry, not all fields are filled out correctly.',
          )
        "
        :actions="false"
        :errors="errors"
        @submit="onSubmit"
      >
        <FormKit
          v-model="email"
          type="text"
          name="email"
          outer-class="mb-48"
          :placeholder="$texts('pdp.emailAddress', 'Enter E-Mail address')"
          validation="required|email"
          :validation-messages="{
            required: $texts('form.fieldRequired', 'This field is required.'),
            email: $texts(
              'form.validEmail',
              'Please enter a valid email address.',
            ),
          }"
        />

        <FormKit
          type="checkbox"
          name="termsAndConditions"
          :value="false"
          validation="accepted"
          validation-visibility="dirty"
          :validation-messages="{
            accepted: $texts(
              'form.acceptTermsAndConditions',
              'Please accept the terms and conditions.',
            ),
          }"
        >
          <template #label="context">
            <span :class="context.classes.label">
              {{ $texts('pdp.acceptTerms', 'I accept') }}
              <DrupalLink
                :to="data.globalConfig.termsOfUseUrl"
                class="underline"
              >
                {{ $texts('pdp.termsAndConditions', 'terms and conditions') }}
              </DrupalLink>
            </span>
          </template>
        </FormKit>

        <FormKit
          type="checkbox"
          name="dataProtection"
          :value="false"
          validation="accepted"
          validation-visibility="dirty"
          :validation-messages="{
            accepted: $texts(
              'form.acceptDataPolicy',
              'Please accept data protection policy.',
            ),
          }"
        >
          <template #label="context">
            <span :class="context.classes.label">
              {{ $texts('pdp.acceptTerms', 'I accept') }}
              <NuxtLink
                :to="data.globalConfig.privacyPolicyUrl"
                class="underline"
              >
                {{ $texts('pdp.dataProtection', 'data protection policy') }}
              </NuxtLink>
            </span>
          </template>
        </FormKit>

        <FormKit type="submit" :disabled="isSubmitting">
          <span v-if="isSubmitting">
            {{ $texts('pdp.notifying', 'Processing...') }}
          </span>
          <span v-else>
            {{ $texts('pdp.notifyMe', 'Notify me') }}
          </span>
        </FormKit>
      </FormKit>
    </div>

    <div v-if="success">
      <h2 class="mb-16 text-3xl text-black">
        {{
          $texts('pdp.productNotificationActive', 'Product notification active')
        }}
      </h2>
      <div class="text-grey-dark-01">
        {{
          $texts(
            'pdp.textProductNotificationActive',
            'Thank you for contacting us. We always have time for you.',
          )
        }}
      </div>
    </div>
  </PageModal>
</template>

<script lang="ts" setup>
const props = defineProps<{
  sku: string
}>()

defineEmits(['close'])

await initFormKit()

const { data } = await useInitData()
const { $texts } = useNuxtApp()
const errors = ref<string[]>([])
const success = ref(false)
const email = ref('')
const isSubmitting = ref(false)

const country = useCurrentCountry()

const { execute } = await useReCaptcha()

async function onSubmit() {
  isSubmitting.value = true
  try {
    const { token } = await execute('notify_me')

    const result = await useGraphqlMutation('createWishlistItem', {
      sku: props.sku,
      email: email.value,
      country: country.value,
      token: token ?? '',
    })

    if (result.data.createWishlistItem?.errors?.length) {
      errors.value = result.data.createWishlistItem.errors.reduce(
        (acc, error) => {
          acc.push(
            error.message.replace(/(<em class="placeholder">|<\/em>)/g, ''),
          )
          return acc
        },
        [],
      )
      return
    }
    success.value = true
    errors.value = []
  } finally {
    isSubmitting.value = false
  }
}
</script>

<script lang="ts">
export default {
  name: 'NotifyMe',
}
</script>
